/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import getErrorBoundary from '~/web/components/ErrorBoundary';
import FailedPostComponent from '~/web/components/ErrorBoundary/failedPostComponent';
import Post from '../../Post';
import Story from '../../Story';
import PostDragWrapper from '../../PostDragWrapper';
import { PostWrapper } from './styles';
import { ProfilesDraft } from '../../../drafts/components/ProfilesDraft';
import { PostTypeReel, PostTypeStory } from '../../../post/constants';

const ErrorBoundary = getErrorBoundary(true);

const PostContentProps = {
  indexType: PropTypes.number.isRequired,
  queueType: PropTypes.string,
  postType: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    isDeleting: PropTypes.bool,
  }).isRequired,
  postPropsType: PropTypes.shape({
    index: PropTypes.number,
    basic: PropTypes.bool,
    draggable: PropTypes.bool,
    onDeleteConfirmClick: PropTypes.func,
    onEditClick: PropTypes.func,
    onShareNowClick: PropTypes.func,
    onRequeueClick: PropTypes.func,
    onDropPost: PropTypes.func,
    onSwapPosts: PropTypes.func,
  }),
  postComponentType: PropTypes.oneOf([Post, Story, ProfilesDraft]).isRequired,
};

const postClassName = (item) => {
  if (!item) return '';

  return [
    'update',
    `post_${item.profile_service}`,
    item.postDetails?.isRetweet ? 'is_retweet' : 'not_retweet',
  ].join(' ');
};

const getPostComponent = ({ queueType, postType }) => {
  if (postType === 'storyGroup') return Story;
  if (queueType === 'drafts') return ProfilesDraft;

  return Post;
};

const getDraftProps = ({ draft, postProps }) => {
  return {
    draftDetails: draft.draftDetails,
    profileService: draft.profile_service,
    geolocationName: draft.service_geolocation_name,
    onDeleteConfirmClick: () => postProps.onDeleteConfirmClick({ post: draft }),
    onEditClick: () => postProps.onEditClick({ post: draft }),
    onApproveClick: () => postProps.onApproveClick({ draft }),
    onRevertApprovalClick: () => postProps.onRevertApprovalClick({ draft }),
    onRequestApprovalClick: () => postProps.onRequestApprovalClick({ draft }),
    onRescheduleClick: () => postProps.onRescheduleClick({ draft }),
  };
};

const getPostProps = ({ post, postProps }) => {
  const campaignId = post.campaignDetails?.id ?? null;

  return {
    service_geolocation_name: post.locationName,
    source_url: post.sourceUrl,
    subprofile_id: post.subprofileID,
    service_user_tags: post.userTags,
    shouldShowEditButton: post.retweet && !!post.retweetComment,
    profileService: post.profile_service,
    channelData: post.channelData,
    isReelsPost: post.updateType === PostTypeReel,
    isStoryPost: post.updateType === PostTypeStory,
    onDeleteConfirmClick: () => postProps.onDeleteConfirmClick({ post }),
    onEditClick: () => postProps.onEditClick({ post }),
    onShareNowClick: () => postProps.onShareNowClick({ post }),
    onRequeueClick: () => postProps.onRequeueClick({ post }),
    onCampaignTagClick: () => postProps.onCampaignTagClick(campaignId),
    onSetRemindersClick: ({ type }) =>
      postProps.onSetRemindersClick({ type, post }),
  };
};

const ErrorBoundaryWrapper = ({ post, fallbackComponent, children }) => (
  <ErrorBoundary
    fallbackComponent={() => (
      <ErrorBoundary
        fallbackComponent={() => (
          <FailedPostComponent key={post.id} post={post} postId={post.id} />
        )}
      >
        {fallbackComponent}
      </ErrorBoundary>
    )}
  >
    {children}
  </ErrorBoundary>
);

ErrorBoundaryWrapper.propTypes = {
  post: PostContentProps.postType,
  fallbackComponent: PropTypes.elementType,
  children: PropTypes.element.isRequired,
};

const DraggablePost = ({
  index,
  post,
  postComponent: PostComponent,
  postProps,
}) => (
  <ErrorBoundaryWrapper
    post={post}
    fallbackComponent={() => (
      <PostDragWrapper
        id={post.id}
        index={index}
        postComponent={PostComponent}
        postProps={postProps}
        basic
      />
    )}
  >
    <PostDragWrapper
      id={post.id}
      post={post}
      index={index}
      postComponent={PostComponent}
      postProps={postProps}
    />
  </ErrorBoundaryWrapper>
);

DraggablePost.propTypes = {
  index: PostContentProps.indexType,
  post: PostContentProps.postType,
  postComponent: PostContentProps.postComponentType,
  postProps: PostContentProps.postPropsType,
};

const NonDraggablePost = ({
  post,
  postComponent: PostComponent,
  postProps,
  isCampaign,
}) => (
  <ErrorBoundaryWrapper
    post={post}
    fallbackComponent={() => (
      <PostComponent
        postId={post.id}
        isCampaign={isCampaign}
        post={post}
        {...postProps}
        basic
      />
    )}
  >
    <PostComponent
      postId={post.id}
      post={post}
      isCampaign={isCampaign}
      {...postProps}
    />
  </ErrorBoundaryWrapper>
);

NonDraggablePost.propTypes = {
  post: PostContentProps.postType,
  postComponent: PostContentProps.postComponentType,
  postProps: PostContentProps.postPropsType,
  isCampaign: PropTypes.bool,
};

const Content = ({
  post,
  queueType,
  postComponent: PostComponent,
  postProps,
  isCampaign,
}) => {
  const itemProps =
    queueType === 'drafts'
      ? getDraftProps({ draft: post, postProps })
      : getPostProps({ post, postProps });

  const postWithEventHandlers = {
    key: post.id,
    ...post,
    ...postProps,
    ...itemProps,
  };

  const { index, basic, draggable } = postProps;

  if (draggable) {
    return (
      <DraggablePost
        index={index}
        post={post}
        postComponent={PostComponent}
        postProps={postWithEventHandlers}
        basic={basic}
      />
    );
  }

  return (
    <NonDraggablePost
      post={post}
      postComponent={PostComponent}
      postProps={postWithEventHandlers}
      isCampaign={isCampaign}
    />
  );
};

Content.propTypes = {
  post: PostContentProps.postType,
  postComponent: PostContentProps.postComponentType,
  queueType: PostContentProps.queueType,
  postProps: PostContentProps.postPropsType,
  isCampaign: PropTypes.bool,
};

const PostContent = ({
  post,
  queueType = 'post',
  analyzeCrossSale,
  isCampaign,
  ...postProps
}) => {
  const PostComponent = getPostComponent({ queueType, postType: post.type });

  return (
    <>
      <PostWrapper
        id={`update-${post.id}`}
        className={postClassName(post)}
        hidden={post.isDeleting}
        data-testid="post-wrapper"
      >
        <Content
          post={post}
          queueType={queueType}
          postComponent={PostComponent}
          isCampaign={isCampaign}
          postProps={postProps}
        />
      </PostWrapper>
    </>
  );
};

PostContent.propTypes = {
  post: PostContentProps.postType,
  queueType: PostContentProps.queueType,
  analyzeCrossSale: PropTypes.bool,
  onShareAgainClick: PropTypes.func,
  postProps: PostContentProps.postPropsType,
  isCampaign: PropTypes.bool,
};

PostContent.defaultProps = {
  analyzeCrossSale: false,
  queueType: 'post',
  post: {},
  onShareAgainClick: () => {},
  isCampaign: false,
  postProps: {
    index: 0,
    draggable: false,
    onDeleteConfirmClick: () => {},
    onEditClick: () => {},
    onShareNowClick: () => {},
    onRequeueClick: () => {},
    onDropPost: () => {},
    onSwapPosts: () => {},
  },
};

export default PostContent;
